import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, type ApiKey } from "../api";
import { useGroups } from "../hooks";
import { useSession } from "../session";

const PERMS = ["logs:write", "logs:read", "errors:write", "errors:read", "errors:manage", "subscriptions:manage", "admin"];

export function KeysPage() {
  const { tenant } = useSession();
  const qc = useQueryClient();
  const groups = useGroups();
  const [form, setForm] = useState<{ name: string; permissions: string[]; group_ids: string[] }>({
    name: "", permissions: ["logs:write", "errors:write"], group_ids: [],
  });
  const [created, setCreated] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const keys = useQuery({
    queryKey: ["keys", tenant],
    queryFn: () => api<{ keys: ApiKey[] }>("/v1/keys").then((r) => r.keys),
  });
  const create = useMutation({
    mutationFn: () =>
      api<ApiKey & { key: string }>("/v1/keys", {
        method: "POST",
        body: { name: form.name, permissions: form.permissions, group_ids: form.group_ids.length ? form.group_ids : undefined },
      }),
    onSuccess: (k) => {
      setCreated(k.key);
      setError(null);
      setForm({ ...form, name: "" });
      qc.invalidateQueries({ queryKey: ["keys"] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/v1/keys/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["keys"] }),
  });
  const toggle = (list: "permissions" | "group_ids", v: string) =>
    setForm((f) => ({ ...f, [list]: f[list].includes(v) ? f[list].filter((x) => x !== v) : [...f[list], v] }));
  const groupName = (id: string) => groups.data?.find((g) => g.id === id)?.name ?? id.slice(0, 8);

  return (
    <section aria-labelledby="keys-title">
      <h1 id="keys-title">API keys</h1>
      <form className="card" onSubmit={(e: FormEvent) => { e.preventDefault(); create.mutate(); }}>
        <h2>New key</h2>
        <label>Name<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></label>
        <fieldset className="row">
          <legend>Permissions</legend>
          {PERMS.map((p) => (
            <label key={p} className="inline"><input type="checkbox" checked={form.permissions.includes(p)} onChange={() => toggle("permissions", p)} /> {p}</label>
          ))}
        </fieldset>
        <fieldset className="row">
          <legend>Limit to groups (none = whole tenant)</legend>
          {groups.data?.map((g) => (
            <label key={g.id} className="inline"><input type="checkbox" checked={form.group_ids.includes(g.id)} onChange={() => toggle("group_ids", g.id)} /> {g.name}</label>
          ))}
        </fieldset>
        <button type="submit" disabled={!form.permissions.length || create.isPending}>Create key</button>
        {error && <p role="alert" className="error">{error}</p>}
        {created && (
          <p role="status" className="secret">
            Copy this key now; it will not be shown again: <code>{created}</code>
          </p>
        )}
      </form>
      <table className="grid">
        <caption className="sr-only">API keys</caption>
        <thead><tr><th scope="col">Name</th><th scope="col">Prefix</th><th scope="col">Permissions</th><th scope="col">Scope</th><th scope="col">Last used</th><th scope="col"><span className="sr-only">Actions</span></th></tr></thead>
        <tbody>
          {keys.data?.map((k) => (
            <tr key={k.id}>
              <td>{k.name}</td>
              <td><code>sf_{k.prefix}_…</code></td>
              <td className="small">{k.permissions.join(", ")}</td>
              <td className="small">
                {k.group_ids ? k.group_ids.map(groupName).join(", ") : k.log_group_ids ? `${k.log_group_ids.length} log group(s)` : "tenant"}
              </td>
              <td className="small">{k.last_used_at ? k.last_used_at.replace("T", " ").slice(0, 19) : "never"}</td>
              <td><button type="button" className="danger" onClick={() => window.confirm(`Revoke ${k.name}?`) && revoke.mutate(k.id)}>Revoke</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
